import React, { useState, useEffect } from 'react';
import SuperAdminLayout from '../../../components/hostel/superadmin/SuperAdminLayout';

const API_URL = process.env.REACT_APP_API_URL || 'https://app.learninghub.ind.in';

const ManageGrievance = ({ onNavigate, userInfo }) => {
  const [loading, setLoading] = useState(true);
  const [grievances, setGrievances] = useState([]);
  const [hostels, setHostels] = useState([]);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterHostel, setFilterHostel] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [selectedGrievance, setSelectedGrievance] = useState(null);
  const [adminResponse, setAdminResponse] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [updating, setUpdating] = useState(false);
  const [downloadingFile, setDownloadingFile] = useState(false);

  useEffect(() => {
    if (userInfo?.userId) {
      fetchGrievances();
      fetchHostels();
    }
  }, [userInfo]);

  const fetchGrievances = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${API_URL}/superadmin/grievances/${userInfo.userId}`);
      const data = await response.json();

      if (data.success) {
        setGrievances(data.grievances || []);
      } else {
        setError(data.message || 'Failed to load grievances');
      }
    } catch (err) {
      console.error('Error fetching grievances:', err);
      setError('Failed to connect to server. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  const fetchHostels = async () => {
    try {
      const response = await fetch(`${API_URL}/superadmin/hostels/${userInfo.userId}`);
      const data = await response.json();

      if (data.success) {
        setHostels(data.hostels || []);
      }
    } catch (err) {
      console.error('Error fetching hostels:', err);
    }
  };

  const handleDownloadFile = async (grievanceId, fileName) => {
    if (downloadingFile) return;

    try {
      setDownloadingFile(true);
      const response = await fetch(
        `${API_URL}/superadmin/grievance/download/${grievanceId}`
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to download file');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
      alert(`Failed to download file: ${error.message}`);
    } finally {
      setDownloadingFile(false);
    }
  };

  const handleViewDetails = (grievance) => {
    setSelectedGrievance(grievance);
    setShowDetailsModal(true);
  };

  const handleOpenResponse = (grievance) => {
    setSelectedGrievance(grievance);
    setNewStatus(grievance.status);
    setAdminResponse(grievance.admin_response || '');
    setShowResponseModal(true);
  };

  const handleUpdateGrievance = async (e) => {
    e.preventDefault();

    if (!newStatus) {
      alert('Please select a status');
      return;
    }

    if (updating) return;

    try {
      setUpdating(true);

      const response = await fetch(
        `${API_URL}/superadmin/grievance/${userInfo.userId}/${selectedGrievance.id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            status: newStatus,
            adminResponse: adminResponse
          })
        }
      );

      const data = await response.json();

      if (data.success) {
        alert('✅ Grievance updated successfully!');
        setShowResponseModal(false);
        setSelectedGrievance(null);
        setAdminResponse('');
        setNewStatus('');
        fetchGrievances();
      } else {
        alert(`❌ ${data.message || 'Failed to update grievance'}`);
      }
    } catch (err) {
      console.error('Error updating grievance:', err);
      alert('❌ Failed to update grievance. Please try again.');
    } finally {
      setUpdating(false);
    }
  };

  const filteredGrievances = grievances.filter(grievance => {
    const matchesSearch =
      grievance.student_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      grievance.grievance_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      grievance.subject?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      grievance.description?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesHostel = filterHostel === 'all' || grievance.hostel_name === filterHostel;
    const matchesStatus = filterStatus === 'all' || grievance.status === filterStatus;
    const matchesCategory = filterCategory === 'all' || grievance.category === filterCategory;
    const matchesPriority = filterPriority === 'all' || grievance.priority === filterPriority;

    return matchesSearch && matchesHostel && matchesStatus && matchesCategory && matchesPriority;
  });

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'pending': return 'status-warning';
      case 'in-progress': return 'status-info';
      case 'resolved': return 'status-active';
      case 'rejected': return 'status-danger';
      default: return 'status-default';
    }
  };

  const getPriorityBadgeClass = (priority) => {
    switch (priority) {
      case 'low': return 'status-info';
      case 'medium': return 'status-warning';
      case 'high': return 'status-danger';
      default: return 'status-default';
    }
  };

  const getCategoryIcon = (category) => {
    const icons = {
      food: '🍽️',
      room: '🛏️',
      maintenance: '🔧',
      cleanliness: '🧹',
      security: '🔒',
      facilities: '🏢',
      staff: '👥',
      other: '📋'
    };
    return icons[category] || '📋';
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  return (
    <SuperAdminLayout
      onNavigate={onNavigate}
      userInfo={userInfo}
      activePage="hostel-superadmin-manage-grievance"
      pageTitle="All Grievances 💬"
    >
      <style>{`
        .stats-row {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
          gap: 20px;
          margin-bottom: 30px;
        }

        .stat-card {
          background: white;
          padding: 24px;
          border-radius: 16px;
          border: 2px solid rgba(82,171,152,0.15);
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .stat-label {
          font-size: 13px;
          color: rgba(43,103,119,0.7);
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .stat-value {
          font-size: 32px;
          font-weight: 900;
          color: #2b6777;
        }

        .controls-section {
          display: flex;
          gap: 20px;
          margin-bottom: 30px;
          flex-wrap: wrap;
          align-items: center;
        }

        .search-box {
          flex: 1;
          min-width: 300px;
          position: relative;
        }

        .search-input {
          width: 100%;
          padding: 14px 20px 14px 50px;
          border: 2px solid rgba(82,171,152,0.2);
          border-radius: 14px;
          font-size: 15px;
          font-family: 'Inter', sans-serif;
          transition: all 0.3s;
          background: white;
        }

        .search-input:focus {
          outline: none;
          border-color: #52ab98;
          box-shadow: 0 0 0 4px rgba(82,171,152,0.1);
        }

        .search-icon {
          position: absolute;
          left: 18px;
          top: 50%;
          transform: translateY(-50%);
          font-size: 20px;
          color: #52ab98;
        }

        .filter-select {
          padding: 14px 20px;
          border: 2px solid rgba(82,171,152,0.2);
          border-radius: 14px;
          font-size: 15px;
          font-family: 'Inter', sans-serif;
          font-weight: 600;
          color: #2b6777;
          background: white;
          cursor: pointer;
          transition: all 0.3s;
        }

        .filter-select:focus {
          outline: none;
          border-color: #52ab98;
          box-shadow: 0 0 0 4px rgba(82,171,152,0.1);
        }

        .grievances-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
          gap: 24px;
        }

        .grievance-card {
          background: white;
          border-radius: 20px;
          padding: 28px;
          border: 2px solid rgba(82,171,152,0.15);
          transition: all 0.3s;
          position: relative;
          overflow: hidden;
        }

        .grievance-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 6px;
          height: 100%;
          background: linear-gradient(180deg, #2b6777 0%, #52ab98 100%);
        }

        .grievance-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 40px rgba(43,103,119,0.15);
          border-color: rgba(82,171,152,0.3);
        }

        .grievance-header {
          display: flex;
          align-items: flex-start;
          gap: 16px;
          margin-bottom: 20px;
        }

        .grievance-icon {
          width: 60px;
          height: 60px;
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          border-radius: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 28px;
          flex-shrink: 0;
        }

        .grievance-info {
          flex: 1;
        }

        .grievance-number {
          font-size: 14px;
          font-weight: 800;
          color: #52ab98;
          margin-bottom: 4px;
        }

        .grievance-student {
          font-size: 18px;
          font-weight: 800;
          color: #2b6777;
          margin-bottom: 4px;
        }

        .grievance-date {
          font-size: 12px;
          color: rgba(43,103,119,0.6);
        }

        .grievance-subject {
          font-size: 16px;
          font-weight: 700;
          color: #2b6777;
          margin-bottom: 10px;
        }

        .grievance-description {
          font-size: 14px;
          color: rgba(43,103,119,0.8);
          line-height: 1.5;
          margin-bottom: 16px;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .grievance-details {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 12px;
          margin-bottom: 20px;
        }

        .detail-item {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .detail-item.full-width {
          grid-column: 1 / -1;
        }

        .detail-label {
          font-size: 11px;
          color: rgba(43,103,119,0.6);
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .detail-value {
          font-size: 13px;
          font-weight: 700;
          color: #2b6777;
        }

        .download-file-btn {
          background: rgba(82,171,152,0.15);
          border: 2px solid rgba(82,171,152,0.3);
          padding: 8px 12px;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 600;
          color: #2b6777;
          cursor: pointer;
          transition: all 0.3s;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          text-align: left;
          word-break: break-word;
        }

        .download-file-btn:hover {
          background: rgba(82,171,152,0.25);
          border-color: #52ab98;
          transform: translateY(-2px);
        }

        .download-file-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .grievance-actions {
          display: flex;
          gap: 10px;
          padding-top: 20px;
          border-top: 2px solid rgba(82,171,152,0.1);
        }

        .action-btn-view,
        .action-btn-respond {
          flex: 1;
          padding: 12px;
          border: none;
          border-radius: 12px;
          font-size: 14px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.3s;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }

        .action-btn-view {
          background: rgba(82,171,152,0.15);
          color: #2b6777;
        }

        .action-btn-view:hover {
          background: rgba(82,171,152,0.25);
          transform: translateY(-2px);
        }

        .action-btn-respond {
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          color: white;
          box-shadow: 0 4px 15px rgba(43,103,119,0.3);
        }

        .action-btn-respond:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 25px rgba(43,103,119,0.4);
        }

        .status-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 6px 12px;
          border-radius: 8px;
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
        }

        .status-active {
          background: rgba(34,197,94,0.15);
          color: #16a34a;
        }

        .status-warning {
          background: rgba(251,146,60,0.15);
          color: #ea580c;
        }

        .status-info {
          background: rgba(59,130,246,0.15);
          color: #2563eb;
        }

        .status-danger {
          background: rgba(239,68,68,0.15);
          color: #dc2626;
        }

        .status-default {
          background: rgba(107,114,128,0.15);
          color: #4b5563;
        }

        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(43,103,119,0.6);
          backdrop-filter: blur(8px);
          z-index: 1000;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
        }

        .modal {
          background: white;
          border-radius: 24px;
          width: 100%;
          max-width: 800px;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 24px 70px rgba(43,103,119,0.3);
        }

        .modal-header {
          padding: 28px 32px;
          border-bottom: 2px solid rgba(82,171,152,0.15);
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .modal-title {
          font-size: 24px;
          font-weight: 900;
          color: #2b6777;
        }

        .modal-close {
          background: rgba(82,171,152,0.15);
          border: none;
          width: 40px;
          height: 40px;
          border-radius: 12px;
          cursor: pointer;
          font-size: 20px;
          color: #2b6777;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s;
        }

        .modal-close:hover {
          background: rgba(82,171,152,0.25);
          transform: rotate(90deg);
        }

        .modal-body {
          padding: 32px;
        }

        .detail-row {
          display: grid;
          grid-template-columns: 180px 1fr;
          gap: 16px;
          padding: 16px 0;
          border-bottom: 1px solid rgba(82,171,152,0.1);
        }

        .detail-row:last-child {
          border-bottom: none;
        }

        .detail-row-label {
          font-size: 13px;
          font-weight: 700;
          color: rgba(43,103,119,0.7);
          text-transform: uppercase;
        }

        .detail-row-value {
          font-size: 15px;
          font-weight: 600;
          color: #2b6777;
          word-break: break-word;
        }

        .form-group {
          margin-bottom: 24px;
        }

        .form-label {
          display: block;
          font-size: 14px;
          font-weight: 700;
          color: #2b6777;
          margin-bottom: 8px;
        }

        .form-select,
        .form-textarea {
          width: 100%;
          padding: 14px 18px;
          border: 2px solid rgba(82,171,152,0.2);
          border-radius: 12px;
          font-size: 15px;
          font-family: 'Inter', sans-serif;
          transition: all 0.3s;
        }

        .form-textarea {
          min-height: 120px;
          resize: vertical;
        }

        .form-select:focus,
        .form-textarea:focus {
          outline: none;
          border-color: #52ab98;
          box-shadow: 0 0 0 4px rgba(82,171,152,0.1);
        }

        .form-select:disabled {
          background: #f5f5f5;
          cursor: not-allowed;
        }

        .modal-footer {
          padding: 24px 32px;
          border-top: 2px solid rgba(82,171,152,0.15);
          display: flex;
          gap: 12px;
          justify-content: flex-end;
        }

        .btn-cancel,
        .btn-submit {
          padding: 14px 28px;
          border: none;
          border-radius: 12px;
          font-size: 15px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.3s;
        }

        .btn-cancel {
          background: rgba(107,114,128,0.15);
          color: #374151;
        }

        .btn-cancel:hover {
          background: rgba(107,114,128,0.25);
        }

        .btn-submit {
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          color: white;
          box-shadow: 0 4px 20px rgba(43,103,119,0.3);
        }

        .btn-submit:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 6px 30px rgba(43,103,119,0.4);
        }

        .btn-submit:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .empty-state {
          text-align: center;
          padding: 80px 20px;
          color: #666;
        }

        .empty-state-icon {
          font-size: 80px;
          margin-bottom: 20px;
        }

        .empty-state-text {
          font-size: 18px;
          color: #2b6777;
          font-weight: 600;
        }

        .file-info {
          background: rgba(82,171,152,0.08);
          padding: 12px;
          border-radius: 10px;
          margin-top: 8px;
        }

        .file-meta {
          font-size: 12px;
          color: rgba(43,103,119,0.7);
          margin-top: 4px;
        }

        @media (max-width: 768px) {
          .grievances-grid {
            grid-template-columns: 1fr;
          }

          .controls-section {
            flex-direction: column;
            align-items: stretch;
          }

          .search-box {
            width: 100%;
          }

          .stats-row {
            grid-template-columns: repeat(2, 1fr);
          }
        }
      `}</style>

      {/* Stats Row */}
      <div className="stats-row">
        <div className="stat-card">
          <div className="stat-label">Total</div>
          <div className="stat-value">{grievances.length}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Pending</div>
          <div className="stat-value">
            {grievances.filter(g => g.status === 'pending').length}
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label">In Progress</div>
          <div className="stat-value">
            {grievances.filter(g => g.status === 'in-progress').length}
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Resolved</div>
          <div className="stat-value">
            {grievances.filter(g => g.status === 'resolved').length}
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label">High Priority</div>
          <div className="stat-value">
            {grievances.filter(g => g.priority === 'high').length}
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="controls-section">
        <div className="search-box">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            placeholder="Search grievances by student, number, subject, or description..."
            className="search-input"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <select
          className="filter-select"
          value={filterHostel}
          onChange={(e) => setFilterHostel(e.target.value)}
        >
          <option value="all">All Hostels</option>
          {hostels.map(hostel => (
            <option key={hostel.hostel_id} value={hostel.hostel_name}>
              {hostel.hostel_name}
            </option>
          ))}
        </select>

        <select
          className="filter-select"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
        >
          <option value="all">All Status</option>
          <option value="pending">⏳ Pending</option>
          <option value="in-progress">🔄 In Progress</option>
          <option value="resolved">✅ Resolved</option>
          <option value="rejected">❌ Rejected</option>
        </select>

        <select
          className="filter-select"
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
        >
          <option value="all">All Categories</option>
          <option value="food">🍽️ Food</option>
          <option value="room">🛏️ Room</option>
          <option value="maintenance">🔧 Maintenance</option>
          <option value="cleanliness">🧹 Cleanliness</option>
          <option value="security">🔒 Security</option>
          <option value="facilities">🏢 Facilities</option>
          <option value="staff">👥 Staff</option>
          <option value="other">📋 Other</option>
        </select>

        <select
          className="filter-select"
          value={filterPriority}
          onChange={(e) => setFilterPriority(e.target.value)}
        >
          <option value="all">All Priorities</option>
          <option value="low">Low</option>
          <option value="medium">Medium</option>
          <option value="high">High</option>
        </select>
      </div>

      {/* Grievances Grid */}
      {loading ? (
        <div className="empty-state">
          <div className="empty-state-icon">⏳</div>
          <div className="empty-state-text">Loading grievances...</div>
        </div>
      ) : error ? (
        <div className="empty-state">
          <div className="empty-state-icon">❌</div>
          <div className="empty-state-text">{error}</div>
        </div>
      ) : filteredGrievances.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">💬</div>
          <div className="empty-state-text">
            {searchQuery || filterHostel !== 'all' || filterStatus !== 'all' || filterCategory !== 'all' || filterPriority !== 'all'
              ? 'No grievances match your filters'
              : 'No grievances found'}
          </div>
        </div>
      ) : (
        <div className="grievances-grid">
          {filteredGrievances.map((grievance) => (
            <div key={grievance.id} className="grievance-card">
              <div className="grievance-header">
                <div className="grievance-icon">
                  {getCategoryIcon(grievance.category)}
                </div>
                <div className="grievance-info">
                  <div className="grievance-number">{grievance.grievance_number}</div>
                  <div className="grievance-student">{grievance.student_name}</div>
                  <div className="grievance-date">
                    {new Date(grievance.created_at).toLocaleDateString()} • {grievance.hostel_name}
                  </div>
                </div>
              </div>

              <div className="grievance-subject">{grievance.subject}</div>
              <div className="grievance-description">{grievance.description}</div>

              <div className="grievance-details">
                <div className="detail-item">
                  <span className="detail-label">Category</span>
                  <span className="detail-value">{grievance.category}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Priority</span>
                  <span className={`status-badge ${getPriorityBadgeClass(grievance.priority)}`}>
                    {grievance.priority}
                  </span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Status</span>
                  <span className={`status-badge ${getStatusBadgeClass(grievance.status)}`}>
                    {grievance.status}
                  </span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Room</span>
                  <span className="detail-value">{grievance.room_number || 'N/A'}</span>
                </div>

                {/* File Attachment */}
                {grievance.file_name && (
                  <div className="detail-item full-width">
                    <span className="detail-label">Attachment</span>
                    <button
                      className="download-file-btn"
                      onClick={() => handleDownloadFile(grievance.id, grievance.file_name)}
                      disabled={downloadingFile}
                      title={`Download ${grievance.file_name}`}
                    >
                      📎 {grievance.file_name}
                      {grievance.file_size && (
                        <span className="file-meta"> ({formatFileSize(grievance.file_size)})</span>
                      )}
                    </button>
                  </div>
                )}
              </div>

              <div className="grievance-actions">
                <button
                  className="action-btn-view"
                  onClick={() => handleViewDetails(grievance)}
                >
                  👁️ View
                </button>
                <button
                  className="action-btn-respond"
                  onClick={() => handleOpenResponse(grievance)}
                >
                  💬 Respond
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedGrievance && (
        <div className="modal-overlay" onClick={() => setShowDetailsModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Grievance Details</h2>
              <button className="modal-close" onClick={() => setShowDetailsModal(false)}>
                ✕
              </button>
            </div>

            <div className="modal-body">
              <div className="detail-row">
                <div className="detail-row-label">Grievance Number</div>
                <div className="detail-row-value">{selectedGrievance.grievance_number}</div>
              </div>
              <div className="detail-row">
                <div className="detail-row-label">Student Name</div>
                <div className="detail-row-value">{selectedGrievance.student_name}</div>
              </div>
              <div className="detail-row">
                <div className="detail-row-label">Phone Number</div>
                <div className="detail-row-value">{selectedGrievance.phone_number}</div>
              </div>
              <div className="detail-row">
                <div className="detail-row-label">Email</div>
                <div className="detail-row-value">{selectedGrievance.email}</div>
              </div>
              <div className="detail-row">
                <div className="detail-row-label">Hostel</div>
                <div className="detail-row-value">{selectedGrievance.hostel_name}</div>
              </div>
              <div className="detail-row">
                <div className="detail-row-label">Room Number</div>
                <div className="detail-row-value">{selectedGrievance.room_number || 'N/A'}</div>
              </div>
              <div className="detail-row">
                <div className="detail-row-label">Category</div>
                <div className="detail-row-value">{selectedGrievance.category}</div>
              </div>
              <div className="detail-row">
                <div className="detail-row-label">Priority</div>
                <div className="detail-row-value">
                  <span className={`status-badge ${getPriorityBadgeClass(selectedGrievance.priority)}`}>
                    {selectedGrievance.priority}
                  </span>
                </div>
              </div>
              <div className="detail-row">
                <div className="detail-row-label">Status</div>
                <div className="detail-row-value">
                  <span className={`status-badge ${getStatusBadgeClass(selectedGrievance.status)}`}>
                    {selectedGrievance.status}
                  </span>
                </div>
              </div>
              <div className="detail-row">
                <div className="detail-row-label">Subject</div>
                <div className="detail-row-value">{selectedGrievance.subject}</div>
              </div>
              <div className="detail-row">
                <div className="detail-row-label">Description</div>
                <div className="detail-row-value">{selectedGrievance.description}</div>
              </div>
              <div className="detail-row">
                <div className="detail-row-label">Submitted On</div>
                <div className="detail-row-value">
                  {new Date(selectedGrievance.created_at).toLocaleString()}
                </div>
              </div>

              {/* File Attachment in Modal */}
              {selectedGrievance.file_name && (
                <div className="detail-row">
                  <div className="detail-row-label">Attachment</div>
                  <div className="detail-row-value">
                    <button
                      className="download-file-btn"
                      onClick={() => handleDownloadFile(selectedGrievance.id, selectedGrievance.file_name)}
                      disabled={downloadingFile}
                    >
                      📎 {downloadingFile ? 'Downloading...' : `Download ${selectedGrievance.file_name}`}
                    </button>
                    {selectedGrievance.file_size && (
                      <div className="file-meta">
                        Size: {formatFileSize(selectedGrievance.file_size)} • Type: {selectedGrievance.file_type || 'Unknown'}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {selectedGrievance.admin_response && (
                <div className="detail-row">
                  <div className="detail-row-label">Admin Response</div>
                  <div className="detail-row-value">{selectedGrievance.admin_response}</div>
                </div>
              )}
              {selectedGrievance.resolved_at && (
                <div className="detail-row">
                  <div className="detail-row-label">Resolved At</div>
                  <div className="detail-row-value">
                    {new Date(selectedGrievance.resolved_at).toLocaleString()}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Response Modal */}
      {showResponseModal && selectedGrievance && (
        <div className="modal-overlay" onClick={() => setShowResponseModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Update Grievance</h2>
              <button className="modal-close" onClick={() => setShowResponseModal(false)}>
                ✕
              </button>
            </div>

            <form onSubmit={handleUpdateGrievance}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Grievance Number</label>
                  <input
                    type="text"
                    className="form-select"
                    value={selectedGrievance.grievance_number}
                    disabled
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Student</label>
                  <input
                    type="text"
                    className="form-select"
                    value={`${selectedGrievance.student_name} (${selectedGrievance.hostel_name})`}
                    disabled
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Status *</label>
                  <select
                    className="form-select"
                    value={newStatus}
                    onChange={(e) => setNewStatus(e.target.value)}
                    required
                    disabled={updating}
                  >
                    <option value="pending">⏳ Pending</option>
                    <option value="in-progress">🔄 In Progress</option>
                    <option value="resolved">✅ Resolved</option>
                    <option value="rejected">❌ Rejected</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Admin Response</label>
                  <textarea
                    className="form-textarea"
                    value={adminResponse}
                    onChange={(e) => setAdminResponse(e.target.value)}
                    placeholder="Enter your response to the student..."
                    disabled={updating}
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => setShowResponseModal(false)}
                  disabled={updating}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-submit"
                  disabled={updating}
                >
                  {updating ? '⏳ Updating...' : '✅ Update Grievance'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </SuperAdminLayout>
  );
};

export default ManageGrievance;